/**
 * Browser WASM VTracer wrapper.
 * Uses `vtracer-wasm` because `@visioncortex/vtracer` is Node-targeted
 * (sync fs.readFileSync for the .wasm) and does not load cleanly in Vite.
 */

export type VectorizeOptions = {
  /** Prefer fewer colors / moderate detail for ornaments */
  binary?: boolean;
  mode?: 'polygon' | 'spline' | 'pixel';
  hierarchical?: 'stacked' | 'cutout';
  filterSpeckle?: number;
  colorPrecision?: number;
  layerDifference?: number;
  cornerThreshold?: number;
  lengthThreshold?: number;
  maxIterations?: number;
  spliceThreshold?: number;
  pathPrecision?: number;
};

/** Sensible defaults for decorative ornaments / flat logos */
export const ORNAMENT_DEFAULTS: Required<VectorizeOptions> = {
  binary: false,
  mode: 'spline',
  hierarchical: 'stacked',
  filterSpeckle: 4,
  colorPrecision: 6,
  layerDifference: 16,
  cornerThreshold: 60,
  lengthThreshold: 4,
  maxIterations: 10,
  spliceThreshold: 45,
  pathPrecision: 8,
};

type VtracerModule = typeof import('vtracer-wasm');

let initPromise: Promise<VtracerModule> | null = null;
let mod: VtracerModule | null = null;

async function ensureWasm(): Promise<VtracerModule> {
  if (mod) return mod;
  if (!initPromise) {
    initPromise = (async () => {
      const imported = await import('vtracer-wasm');
      // Package default looks for vtracer_bg.wasm but ships vtracer.wasm — pass explicit URL.
      const wasmUrl = (await import('vtracer-wasm/vtracer.wasm?url')).default;
      await imported.default({ module_or_path: wasmUrl });
      mod = imported;
      return imported;
    })().catch((err) => {
      initPromise = null;
      throw err;
    });
  }
  return initPromise;
}

function toConfig(opts?: VectorizeOptions): Record<string, unknown> {
  const o = { ...ORNAMENT_DEFAULTS, ...opts };
  return {
    binary: o.binary,
    mode: o.mode,
    hierarchical: o.hierarchical,
    cornerThreshold: o.cornerThreshold,
    lengthThreshold: o.lengthThreshold,
    maxIterations: o.maxIterations,
    spliceThreshold: o.spliceThreshold,
    filterSpeckle: o.filterSpeckle,
    colorPrecision: o.colorPrecision,
    layerDifference: o.layerDifference,
    pathPrecision: o.pathPrecision,
  };
}

function canvasFromSource(
  source: ImageBitmap | HTMLCanvasElement | HTMLImageElement | OffscreenCanvas,
): HTMLCanvasElement {
  if (typeof HTMLCanvasElement !== 'undefined' && source instanceof HTMLCanvasElement) {
    return source;
  }
  const w = Number((source as ImageBitmap).width || (source as HTMLImageElement).naturalWidth || 1);
  const h = Number((source as ImageBitmap).height || (source as HTMLImageElement).naturalHeight || 1);
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, w);
  canvas.height = Math.max(1, h);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D unavailable');
  ctx.drawImage(source as CanvasImageSource, 0, 0);
  return canvas;
}

function rgbaFromCanvas(canvas: HTMLCanvasElement): {
  rgba: Uint8Array;
  width: number;
  height: number;
} {
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('Canvas 2D unavailable');
  const { width, height } = canvas;
  const { data } = ctx.getImageData(0, 0, width, height);
  return {
    rgba: new Uint8Array(data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength)),
    width,
    height,
  };
}

/**
 * Lazy-init WASM once, then vectorize a bitmap/canvas/image to SVG markup.
 */
export async function vectorizeImageBitmapOrCanvas(
  source: ImageBitmap | HTMLCanvasElement | HTMLImageElement | OffscreenCanvas,
  opts?: VectorizeOptions,
): Promise<string> {
  const api = await ensureWasm();
  const canvas = canvasFromSource(source);
  const { rgba, width, height } = rgbaFromCanvas(canvas);
  return api.to_svg(rgba, width, height, toConfig(opts));
}

/** Vectorize raw RGBA8 pixels. */
export async function vectorizeRgba(
  rgba: Uint8Array,
  width: number,
  height: number,
  opts?: VectorizeOptions,
): Promise<string> {
  const api = await ensureWasm();
  return api.to_svg(rgba, width, height, toConfig(opts));
}

/** Preload WASM (optional; first vectorize also inits). */
export async function preloadVtracer(): Promise<void> {
  await ensureWasm();
}
